import Button from 'tailwind-components/Button/Button';
import icons from 'constants/icons';
import useAgentsCommissions from 'hooks/useAgentsCommissions';
import useTabUrlState from 'hooks/useTabUrlState';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import AgentsCommissionsTableFilters from './AgentsCommissionsTableFilters/AgentsCommissionsTableFilters';
import SimpleFilter from './AgentsCommissionsTableFilters/SimpleFilter/SimpleFilter';
import CommissionsKpiCards, {
  COMMISSION_TYPE_TABS
} from './Components/CommissionsKpiCards';
import { CommissionsGraph } from './Components/CommissionsGraph';
import CommissionsPieChart from './Components/CommissionsPieChart';
import CommissionsMap from './Components/CommissionsMap';
import CommissionsTable from './Components/CommissionsTable';
// MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
// import MembercountTable from './Components/MembercountTable';
const ArrowRight = icons['arrowRight'];

const DashboardCommissions = ({ setLocalLoader }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filterCount, setFilterCount] = useState(1);

  const {
    data_for_graph,
    data_for_table,
    months,
    formData,
    setformData,
    clearFilters,
    applyFilters,
    handleChangeSelectValue,
    // MEMBER_COUNT_HIDDEN
    // data_for_table_member,
    data_for_state_commissions,
    carrierCommissionsColors
  } = useAgentsCommissions({
    setLocalLoader,
    setFilterCount,
    setShowFilters
  });

  // commission type tab synced to the URL (?type); keeps the data filter in sync
  const [commissionType, setCommissionType] = useTabUrlState({
    param: 'type',
    tabIds: COMMISSION_TYPE_TABS.map(t => t.id),
    defaultTabId: 'All',
    correctInvalidToDefault: true
  });

  useEffect(() => {
    if (formData.commission_type !== commissionType) {
      handleChangeSelectValue(commissionType, 'commission_type');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissionType]);

  return (
    <div className='flex flex-col gap-4 mt-2 pt-1'>
      {/* Toolbar: Show Filters + Date type */}
      <div className='flex flex-row items-center justify-between gap-2' data-tour='commissions-toolbar'>
        <Button
          text={`${
            showFilters ? 'Hide Filters' : 'Show Filters'
          } (${filterCount})`}
          size='medium'
          variant='primaryDarkened'
          data-tour='commissions-show-filters'
          icon={
            <ArrowRight
              className={
                showFilters
                  ? 'transition-transform duration-200 rotate-90'
                  : 'transition-transform duration-200'
              }
            />
          }
          onClick={() => setShowFilters(!showFilters)}
        />
        <SimpleFilter
          showCount={false}
          multiple={false}
          options={['Coverage Month', 'Statement Date', 'Statement Month']}
          value={[formData.date_type]}
          handleChange={event => {
            const {
              target: { value }
            } = event;
            handleChangeSelectValue(value, 'date_type');
          }}
        />
      </div>

      {/* Main layout: filters sidebar + content */}
      <div className='flex flex-col lg:flex-row gap-4 items-start'>
        {/* Filters sidebar — always mounted, hidden via CSS so effects keep running */}
        <div
          data-tour='commissions-filters'
          className={`shrink-0 w-full lg:w-1/3 xl:w-1/4 ${
            showFilters ? '' : 'hidden'
          }`}
        >
          <AgentsCommissionsTableFilters
            filterCount={filterCount}
            setFilterCount={setFilterCount}
            formData={formData}
            setformData={setformData}
            clearFilters={clearFilters}
            handleChangeSelectValue={handleChangeSelectValue}
            applyFilters={applyFilters}
            showFilters={showFilters}
          />
        </div>

        {/* Content column */}
        <div className='flex flex-col gap-4 flex-1 min-w-0'>
          {/* KPI Cards: Total + tabs + YTD / Members / Top Carrier */}
          <div data-tour='commissions-kpi'>
          <CommissionsKpiCards
            data_for_graph={data_for_graph}
            commissionType={commissionType}
            onCommissionTypeChange={setCommissionType}
          />
          </div>

          <div data-tour='commissions-graph' className='rounded-[14px] border border-[#d9d9da] w-full bg-white flex flex-col'>
            <span className='px-4 pt-4'>
              By: <span className='font-semibold'>{formData.date_type}</span>
            </span>
            <CommissionsGraph
              data_for_graph={data_for_graph}
              carrierColors={carrierCommissionsColors}
            />
          </div>
        </div>
      </div>

      {/* Pie chart + Map side by side */}
      <div className='flex flex-col lg:flex-row items-start gap-4 z-50'>
        <div data-tour='commissions-pie' className='w-full lg:w-5/12 flex'>
          <div className='rounded-[14px] border border-[#d9d9da] w-full bg-white flex flex-col'>
            <span className='px-4 pt-4 font-semibold text-gray-700'>
              Breakdown by Top Carriers
            </span>
            <CommissionsPieChart
              data_for_graph={data_for_graph}
              carrierColors={carrierCommissionsColors}
            />
          </div>
        </div>
        <div className='w-full lg:w-7/12 flex'>
          <CommissionsMap
            data_for_state_commissions={data_for_state_commissions}
          />
        </div>
      </div>

      {/* Tables */}
      <div className='flex flex-col gap-4'>
        <div data-tour='commissions-table' className='rounded-[14px] border border-[#d9d9da] w-full bg-white flex flex-col overflow-hidden'>
          <span className='mt-2.5 ml-2.5'>
            Commissions By:{' '}
            <span className='font-semibold'>{formData.date_type}</span>
          </span>
          <CommissionsTable data_for_table={data_for_table} months={months} />
        </div>

        {/* MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
        <div data-tour='commissions-members' className='rounded-[14px] border border-[#d9d9da] w-full bg-white flex flex-col overflow-hidden'>
          <span className='pt-2.5 ml-2.5'>
            Member Count By:{' '}
            <span className='font-semibold'>{formData.date_type}</span>
          </span>
          <MembercountTable
            data_for_table_member={data_for_table_member}
            months={months}
          />
        </div>
        */}
      </div>
    </div>
  );
};

DashboardCommissions.propTypes = {
  setLocalLoader: PropTypes.func
};

export default DashboardCommissions;
